// App.tsx
import React from "react";
import api from "./api/client";
import MotorScheduler from "./MotorScheduler";
import Overview from "./Overview";

type MeResponse = {
  user?: { id: string; email: string; name?: string | null };
  membership?: { orgId: string; role: "ADMIN" | "PLANNER" | "VIEWER" };
};

type Tab = "prehled" | "planner";

function readTabFromHash(): Tab {
  const h = (typeof window !== "undefined" ? window.location.hash : "").replace("#", "").toLowerCase();
  return h === "planner" ? "planner" : "prehled";
}

function setAuthToken(token?: string) {
  const common = (api.defaults.headers as any).common ?? ((api.defaults.headers as any).common = {});
  if (token) common["Authorization"] = `Bearer ${token}`;
  else delete common["Authorization"];
}

/** Hezčí a bezpečnější převod chyb z Axiosu/proxy/BE do jedné věty */
function normalizeError(e: any): string {
  const status = e?.response?.status;
  const data = e?.response?.data;

  if (typeof data === "string") {
    if (/^</.test(data)) return "Server vrátil neočekávanou odpověď.";
    const low = data.toLowerCase();
    if (status === 400 || status === 401 || /invalid|credential|password|not\s*found/.test(low)) {
      return "Nesprávný e-mail nebo heslo.";
    }
    return data;
  }

  if (data && typeof data?.error === "string") {
    const t = data.error.toLowerCase();
    if (status === 400 || status === 401 || /invalid|credential|password|not\s*found/.test(t)) {
      return "Nesprávný e-mail nebo heslo.";
    }
    return data.error;
  }

  if (data?.formErrors || data?.fieldErrors) return "Neplatná data ve formuláři.";
  if (status === 400 || status === 401) return "Nesprávný e-mail nebo heslo.";
  if (status === 403) return "Přístup byl zamítnut.";
  if (status === 404) return "Služba nenalezena.";
  if (status >= 500) return "Chyba na serveru. Zkuste to prosím znovu.";
  if (e?.request && !e?.response) return "Server je nedostupný. Zkontroluj připojení.";
  return e?.message || "Přihlášení se nezdařilo.";
}

function useAuth() {
  const [status, setStatus] = React.useState<"checking" | "anon" | "authed">("checking");
  const [me, setMe] = React.useState<MeResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // cookie-first → token fallback (s cache-busterem)
  React.useEffect(() => {
    (async () => {
      try {
        // 1) zkus cookie session
        try {
          const res = await api.get<MeResponse>("/me", { withCredentials: true, params: { _ts: Date.now() } });
          setMe(res.data || null);
          setStatus("authed");
          return;
        } catch {
          /* zkusíme token níže */
        }

        // 2) token fallback z localStorage
        const token = localStorage.getItem("auth.token") || undefined;
        if (token) setAuthToken(token);
        if (token) {
          try {
            const res = await api.get<MeResponse>("/me", { withCredentials: true, params: { _ts: Date.now() } });
            setMe(res.data || null);
            setStatus("authed");
            return;
          } catch {
            localStorage.removeItem("auth.token");
            setAuthToken(undefined);
          }
        }

        setStatus("anon");
      } catch {
        setStatus("anon");
      }
    })();
  }, []);

  const login = React.useCallback(async (email: string, password: string) => {
    setError(null);
    // před loginem zahoď případný starý bearer
    setAuthToken(undefined);

    try {
      // přihlášení – BE může vrátit token (JWT), ale i nemusí (pouze cookie)
      const res = await api.post<any>("/auth/login", { email, password }, { withCredentials: true });

      const token: string | undefined = res.data?.token || res.data?.accessToken || res.data?.jwt;
      if (token) {
        localStorage.setItem("auth.token", token);
        setAuthToken(token);
      } else {
        localStorage.removeItem("auth.token");
        setAuthToken(undefined);
      }

      // ověř identitu – teprve potom povýšíme do "authed"
      const meRes = await api.get<MeResponse>("/me", { withCredentials: true, params: { _ts: Date.now() } });
      setMe(meRes.data || null);
      setStatus("authed");
    } catch (e: any) {
      // čistka při chybě
      try { localStorage.removeItem("auth.token"); } catch {}
      setAuthToken(undefined);
      setStatus("anon");
      setError(normalizeError(e));
    }
  }, []);

// v App.tsx – v useAuth()
const logout = React.useCallback(async () => {
  try {
    await api.post("/auth/logout", {}, { withCredentials: true, params: { _ts: Date.now() } }).catch(() => {});
  } finally {
    localStorage.removeItem("auth.token");
    try { delete (api.defaults.headers as any).common?.Authorization; } catch {}
    setMe(null);
    setStatus("anon");
    // tvrdý reload aplikace (zamezí cache/ghost session)
    window.location.replace("/app/");
  }
}, []);

  return { status, me, login, logout, error, setError };
}

function LoginPage({
  onSubmit, error, clearingError,
}: { onSubmit: (email: string, password: string) => Promise<void>; error?: string | null; clearingError: () => void; }) {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const msg = typeof error === "string" ? error : (error ? "Přihlášení se nezdařilo." : null);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" lang="cs">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow">
        <h1 className="mb-1 text-2xl font-semibold">Přihlášení</h1>
        <p className="mb-6 text-sm text-gray-600">Zadej své přístupové údaje.</p>

        {msg && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {msg}
          </div>
        )}

        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (busy) return;
            setBusy(true);
            try { await onSubmit(email.trim(), password); }
            finally { setBusy(false); }
          }}
          className="space-y-3"
        >
          <div>
            <label className="mb-1 block text-sm text-gray-700">E-mail</label>
            <input
              className="w-full rounded-xl border px-3 py-2"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => { if (error) clearingError(); setEmail(e.target.value); }}
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-700">Heslo</label>
            <input
              className="w-full rounded-xl border px-3 py-2"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => { if (error) clearingError(); setPassword(e.target.value); }}
              required
            />
          </div>

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-2xl bg-emerald-600 px-4 py-2 text-white transition hover:bg-emerald-700 disabled:opacity-60"
          >
            {busy ? "Přihlašuji…" : "Přihlásit"}
          </button>
        </form>
      </div>
    </div>
  );
}

function TopBar({ me, onLogout }: { me: MeResponse | null; onLogout: () => void }) {
  const name = me?.user?.name || me?.user?.email || "Uživatel";
  const role = me?.membership?.role;
  return (
    <div className="sticky top-0 z-30 border-b bg-white/80 backdrop-blur">
      <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-3 px-4 py-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Přihlášen:</span>
          <span className="text-sm font-medium">{name}</span>
          {role && <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{role}</span>}
        </div>
        <button className="rounded-xl border px-3 py-1.5 text-sm hover:bg-gray-50" onClick={onLogout}>
          Odhlásit
        </button>
      </div>
    </div>
  );
}

export default function App() {
  const { status, me, login, logout, error, setError } = useAuth();

  // jednoduchý hash-router mezi „Přehled“ a „Planner“
  const [tab, setTab] = React.useState<Tab>(() => readTabFromHash());
  React.useEffect(() => {
    const onHash = () => setTab(readTabFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  function go(next: Tab) {
    if (next !== tab) {
      window.location.hash = next;
      setTab(next);
    }
  }

  if (status === "checking") {
    return (
      <div className="min-h-screen grid place-items-center bg-white">
        <div className="rounded-xl border px-4 py-2 text-sm text-gray-600 shadow-sm">
          Ověřuji přihlášení…
        </div>
      </div>
    );
  }

  if (status === "anon") {
    return <LoginPage onSubmit={login} error={error} clearingError={() => setError(null)} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar me={me} onLogout={logout} />

      {/* Menu Přehled / Planner */}
      <div className="sticky top-[48px] z-20 border-b bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/70">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 py-3">
          <div className="text-lg font-semibold">GreenSync</div>
          <div className="flex items-center gap-2 rounded-2xl border p-1">
            <button
              onClick={() => go("prehled")}
              className={`rounded-2xl px-3 py-1 text-sm ${tab === "prehled" ? "bg-gray-900 text-white" : "hover:bg-gray-100"}`}
            >
              Přehled
            </button>
            <button
              onClick={() => go("planner")}
              className={`rounded-2xl px-3 py-1 text-sm ${tab === "planner" ? "bg-gray-900 text-white" : "hover:bg-gray-100"}`}
            >
              Planner
            </button>
          </div>
        </div>
      </div>

      {/* Obsah */}
      <div className="mx-auto max-w-[1400px] p-4">
        {tab === "prehled" ? <Overview /> : <MotorScheduler />}
      </div>
    </div>
  );
}
